import axios from "axios";
import { PeriodFrequency } from "../types/PeriodFrequency";
import getFrequencyID from "../ducks/products/utils/getFrequencyID";

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
});

const adminApi = axios.create({
  baseURL: `${process.env.REACT_APP_ADMIN_API_URL}/admin/`,
  timeout: 20000,
});

const dataAPI = {
  fetchNavigationMenu: () => adminApi.get("Menu"),

  fetchMenu: (menuID: number) => adminApi.get(`Product?menuItemId=${menuID}`),

  fetchProduct: (
    productID: string,
    options?: { frequency?: PeriodFrequency; area?: number }
  ) => {
    let URI = `Product/${productID}`;
    if (options) {
      if (options.frequency && options.area) {
        const frequencyID = getFrequencyID(options.frequency);
        URI = `Product/${productID}?frequencyID=${frequencyID}&areaID=${options.area}`;
      } else if (options.area) {
        URI = `Product/${productID}?areaID=${options.area}`;
      } else if (options.frequency) {
        const frequencyID = getFrequencyID(options.frequency);
        URI = `Product/${productID}?frequencyID=${frequencyID}`;
      }
    }
    return adminApi.get(URI);
  },

  fetchFrequencyRecord: ({
    areaID,
    frequency,
    from,
    productID,
    to,
    skipFor,
  }: {
    areaID: string | number;
    frequency: PeriodFrequency;
    from: string;
    productID: string;
    to: string;
    skipFor?: number;
  }) =>
    api.get(`Value_${frequency}?$filter=ProductID eq ${productID} and AreaID eq ${areaID} and Active eq 1 and
    ReportPeriod ge ${convertToISO8601(
      from
    )} and ReportPeriod le ${convertToISO8601(to)}
    &$select=AreaID,Value,ReportPeriod,CollectionDate${
      frequency === "Weekly" ? ",WeekEnding" : ""
    }
    &$orderby=ReportPeriod asc, CollectionDate desc${
      skipFor ? `&$skip=${skipFor}` : ""
    }`),

  fetchUsaFrequencyRecord: ({
    frequency,
    from,
    productID,
    to,
    skipFor,
  }: {
    frequency: PeriodFrequency;
    from: string;
    productID: string;
    to: string;
    skipFor?: number;
  }) =>
    api.get(`Value_${frequency}?$filter=ProductID eq ${productID} and AreaID le 53 and Active eq 1 and
      ReportPeriod ge ${convertToISO8601(
        from
      )} and ReportPeriod le ${convertToISO8601(to)}
      &$select=AreaID,Value,ReportPeriod,CollectionDate&$orderby=ReportPeriod asc, CollectionDate desc${
        skipFor ? `&$skip=${skipFor}` : ""
      }`),

  fetchRecordsForUnitConversion: ({
    frequency,
    productID,
    skipFor,
  }: {
    frequency: PeriodFrequency;
    productID: string;
    skipFor?: number;
  }) =>
    api.get(`Value_${frequency}?$filter=ProductID eq ${productID}
    &$select=AreaID,Value,ReportPeriod&$orderby=ReportPeriod asc, CollectionDate desc${
      skipFor ? `&$skip=${skipFor}` : ""
    }`),

  fetchFuturesRecordWithTradeDate: ({
    from,
    fromTradingDate,
    productID,
    to,
    toTradingDate,
    skipFor,
  }: {
    from: string;
    fromTradingDate: string;
    productID: string;
    to: string;
    toTradingDate: string;
    skipFor?: number;
  }) =>
    api.get(`Value_Futures?$filter=ContractMonth
   ge ${convertToISO8601(from)} and ContractMonth le ${convertToISO8601(
      to
    )} and ProductID eq
   ${productID} and ReportPeriod ge ${convertToISO8601(
      fromTradingDate
    )} and ReportPeriod le ${convertToISO8601(toTradingDate)}
   &$select=ProductID,AreaID,OpenPrice,HighPrice,LowPrice,ClosePrice,Volume,ReportPeriod,
   ContractMonth,SourceID,OpenInterest&$orderby=ContractMonth, ReportPeriod asc${
     skipFor ? `&$skip=${skipFor}` : ""
   }`),

  fetchFuturesRecordWithContractMonth: ({
    from,
    productID,
    to,
    skipFor,
  }: {
    from: string;
    productID: string;
    to: string;
    skipFor?: number;
  }) =>
    api.get(`Value_Futures?$filter=ContractMonth
   ge ${convertToISO8601(from)} and ContractMonth le ${convertToISO8601(
      to
    )} and ProductID eq
   ${productID}&$select=ProductID,AreaID,OpenPrice,HighPrice,LowPrice,ClosePrice,Volume,ReportPeriod,
   ContractMonth,SourceID,OpenInterest&$orderby=ContractMonth asc, ReportPeriod asc${
     skipFor ? `&$skip=${skipFor}` : ""
   }`),

  fetchFuturesRecordWithNearbyIndex: ({
    from,
    index,
    productID,
    to,
    skipFor,
  }: {
    from: string;
    index: number;
    productID: string;
    to: string;
    skipFor?: number;
  }) =>
    api.get(`Value_Futures?$filter=ContractMonth
   ge ${convertToISO8601(from)} and ContractMonth le ${convertToISO8601(
      to
    )} and ProductID eq ${productID} and NearbyIndex eq ${index}
   &$select=ProductID,AreaID,OpenPrice,HighPrice,LowPrice,ClosePrice,Volume,ReportPeriod,
   ContractMonth,SourceID,OpenInterest&$orderby=ContractMonth asc, ReportPeriod asc${
     skipFor ? `&$skip=${skipFor}` : ""
   }`),

  fetchUnits: () => adminApi.get("unit/conversion"),

  // filters
  getPublishers: () => api.get("UDM_Catalog_Publishers"),
  getReports: () => api.get("UDM_Catalog_Reports"),
  getAreas: () => api.get("UDM_Catalog_Areas"),
  getCategories: () => api.get("UDM_Catalog_Categories"),
  getMeasures: () => api.get("UDM_Catalog_Measures"),
  // https://udmdirect-odata-develop.azurewebsites.net/web/area/classification
  getCounties: () => adminApi.get("area/ISO 3166-1"),
  // Maybe for later :D
  getDashboards: () => api.get("UDM_Catalog_Dashboards"),
  getSources: () => api.get("UDM_Catalog_Sources"),
  getTables: () => api.get("UDM_Catalog_Tables"),
  getUnits: () => api.get("UDM_Catalog_Units"),
};

export default dataAPI;

function convertToISO8601(dateString: string): string {
  // Temporary fallback to original implementation to debug
  const parts = dateString.split("-");
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    const utcDate = new Date(Date.UTC(year, month, day, 0, 0, 0));
    const result = utcDate.toISOString().split(".")[0] + "Z";
    return result;
  }

  // Fallback for other formats
  const date = new Date(dateString);
  const result = date.toISOString().split(".")[0] + "Z";
  return result;
}
